import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { Article, ArticleCategory, ArticleTag, ArticleSource, ArticleFAQ, InteractiveContentRef, ArticleKeyTakeaways } from '@/types/articles';

// Helper to transform database response to Article type
const transformArticle = (data: any): Article => ({
  ...data,
  sources: (data.sources as ArticleSource[]) || [],
  faqs: (data.faqs as ArticleFAQ[]) || [],
  interactive_content_ids: (data.interactive_content_ids as InteractiveContentRef[]) || [],
  key_takeaways: (data.key_takeaways as ArticleKeyTakeaways) || null
});

// Extract interactive content references (polls, quizzes, mini-games) from HTML content
export const extractInteractiveContent = (html: string | null): InteractiveContentRef[] => {
  if (!html) return [];
  const refs: InteractiveContentRef[] = [];
  const seen = new Set<string>();
  // Match iframes with /poll/, /quiz/, or /mini-game/ URLs
  const iframeRegex = /src=["'][^"']*\/(poll|quiz|mini-game)\/([^"'\/\s?#]+)/gi;
  let match;
  while ((match = iframeRegex.exec(html)) !== null) {
    const type = match[1].toLowerCase() as InteractiveContentRef['type'];
    const id = match[2];
    const key = `${type}:${id}`;
    if (!seen.has(key)) {
      seen.add(key);
      refs.push({ id, type });
    }
  }
  return refs;
};

export const useArticles = (brandId: string | undefined) => {
  return useQuery({
    queryKey: ['articles', brandId],
    queryFn: async () => {
      if (!brandId) return [];
      
      const { data, error } = await supabase
        .from('os_articles')
        .select('*')
        .eq('brand_id', brandId)
        .order('updated_at', { ascending: false });
      
      if (error) throw error;
      return (data || []).map(transformArticle);
    },
    enabled: !!brandId
  });
};

export const useArticle = (articleId: string | undefined) => {
  return useQuery({
    queryKey: ['article', articleId],
    queryFn: async () => {
      if (!articleId) return null;
      
      const { data, error } = await supabase
        .from('os_articles')
        .select('*')
        .eq('id', articleId)
        .single();
      
      if (error) throw error;
      return transformArticle(data);
    },
    enabled: !!articleId
  });
};

export const useCreateArticle = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (article: Partial<Article> & { brand_id: string; title: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { sources, faqs, key_takeaways, ...rest } = article;
      const insertData = {
        ...rest,
        sources: (sources ?? []) as any,
        faqs: (faqs ?? []) as any,
        key_takeaways: key_takeaways ? JSON.parse(JSON.stringify(key_takeaways)) : null,
        user_id: user?.id
      };
      
      const { data, error } = await supabase
        .from('os_articles')
        .insert(insertData as any)
        .select()
        .single();
      
      if (error) throw error;
      return transformArticle(data);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['articles', data.brand_id] });
      toast.success('Article created');
    },
    onError: (error) => {
      console.error('Error creating article:', error);
      toast.error('Failed to create article');
    }
  });
};

export const useUpdateArticle = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, sources, faqs, interactive_content_ids, key_takeaways, ...updates }: Partial<Article> & { id: string }) => {
      const updateData = {
        ...updates,
        sources: (sources ?? []) as any,
        faqs: (faqs ?? []) as any,
        interactive_content_ids: (interactive_content_ids ?? []) as any,
        key_takeaways: key_takeaways ? JSON.parse(JSON.stringify(key_takeaways)) : null
      };
      
      const { data, error } = await supabase
        .from('os_articles')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return transformArticle(data);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['articles', data.brand_id] });
      queryClient.invalidateQueries({ queryKey: ['article', data.id] });
    },
    onError: (error) => {
      console.error('Error updating article:', error);
      toast.error('Failed to save article');
    }
  });
};

export const useDeleteArticle = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, brandId }: { id: string; brandId: string }) => {
      const { error } = await supabase
        .from('os_articles')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      return { id, brandId };
    },
    onSuccess: ({ brandId }) => {
      queryClient.invalidateQueries({ queryKey: ['articles', brandId] });
      toast.success('Article deleted');
    },
    onError: (error) => {
      console.error('Error deleting article:', error);
      toast.error('Failed to delete article');
    }
  });
};

// Categories
export const useArticleCategories = (brandId: string | undefined) => {
  return useQuery({
    queryKey: ['article-categories', brandId],
    queryFn: async () => {
      if (!brandId) return [];
      
      const { data, error } = await supabase
        .from('os_article_categories')
        .select('*')
        .eq('brand_id', brandId)
        .order('sort_order', { ascending: true });
      
      if (error) throw error;
      return data as ArticleCategory[];
    },
    enabled: !!brandId
  });
};

export const useCreateArticleCategory = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (category: Partial<ArticleCategory> & { brand_id: string; name: string }) => {
      const slug = category.slug || category.name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
      
      const { data, error } = await supabase
        .from('os_article_categories')
        .insert({ ...category, slug })
        .select()
        .single();
      
      if (error) throw error;
      return data as ArticleCategory;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['article-categories', data.brand_id] });
    },
    onError: (error) => {
      console.error('Error creating category:', error);
      toast.error('Failed to create category');
    }
  });
};

export const useUpdateArticleCategory = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<ArticleCategory> & { id: string }) => {
      const { data, error } = await supabase
        .from('os_article_categories')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data as ArticleCategory;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['article-categories', data.brand_id] });
    },
    onError: (error) => {
      console.error('Error updating category:', error);
      toast.error('Failed to update category');
    }
  });
};

export const useDeleteArticleCategory = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      // First get the category to know its brand_id
      const { data: category } = await supabase
        .from('os_article_categories')
        .select('brand_id')
        .eq('id', id)
        .single();
      
      const { error } = await supabase
        .from('os_article_categories')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      return { brandId: category?.brand_id };
    },
    onSuccess: ({ brandId }) => {
      if (brandId) {
        queryClient.invalidateQueries({ queryKey: ['article-categories', brandId] });
      }
    },
    onError: (error) => {
      console.error('Error deleting category:', error);
      toast.error('Failed to delete category');
    }
  });
};

// Tags
export const useArticleTags = (brandId: string | undefined) => {
  return useQuery({
    queryKey: ['article-tags', brandId],
    queryFn: async () => {
      if (!brandId) return [];
      
      const { data, error } = await supabase
        .from('os_article_tags')
        .select('*')
        .eq('brand_id', brandId)
        .order('name', { ascending: true });
      
      if (error) throw error;
      return data as ArticleTag[];
    },
    enabled: !!brandId
  });
};

export const useCreateArticleTag = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (tag: Partial<ArticleTag> & { brand_id: string; name: string }) => {
      const slug = tag.slug || tag.name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
      
      const { data, error } = await supabase
        .from('os_article_tags')
        .insert({ ...tag, slug })
        .select()
        .single();
      
      if (error) throw error;
      return data as ArticleTag;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['article-tags', data.brand_id] });
    },
    onError: (error) => {
      console.error('Error creating tag:', error);
      toast.error('Failed to create tag');
    }
  });
};

export const useUpdateArticleTag = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<ArticleTag> & { id: string }) => {
      const { data, error } = await supabase
        .from('os_article_tags')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data as ArticleTag;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['article-tags', data.brand_id] });
    },
    onError: (error) => {
      console.error('Error updating tag:', error);
      toast.error('Failed to update tag');
    }
  });
};

export const useDeleteArticleTag = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      // First get the tag to know its brand_id
      const { data: tag } = await supabase
        .from('os_article_tags')
        .select('brand_id')
        .eq('id', id)
        .single();
      
      const { error } = await supabase
        .from('os_article_tags')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      return { brandId: tag?.brand_id };
    },
    onSuccess: ({ brandId }) => {
      if (brandId) {
        queryClient.invalidateQueries({ queryKey: ['article-tags', brandId] });
      }
    },
    onError: (error) => {
      console.error('Error deleting tag:', error);
      toast.error('Failed to delete tag');
    }
  });
};
